import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ANIMATION_FINISHED,
  ANIMATION_STOPPED,
  INITIALIZED,
} from '../TextParticles.types';
import type {
  WorkerMessage,
  WorkerMessageTypes,
  ParticleRendererProps,
} from '../TextParticles.types';
import type { getMainThreadMessages } from '../worker/messages';
import { isImageTransferred } from '../utils/image-utils';

type UseParticleRendererProps = {
  image: ImageBitmap | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onAnimationEnd: () => void;
  componentId: string;
  worker: Worker | null;
  messages: ReturnType<typeof getMainThreadMessages>;
  direction: number;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
  windDirection: number;
  windSpeed: number;
  turbulence: number;
  onInitialized: () => void;
  onAnimationStopped: () => void;
} & ParticleRendererProps;

export const useParticleRenderer = ({
  text,
  animationType,
  radius,
  duration,
  canvasRef,
  image,
  onAnimationEnd,
  componentId,
  worker,
  messages,
  direction,
  particleStartSize,
  particleEndSize,
  particleStartOpacity,
  particleEndOpacity,
  windDirection,
  windSpeed,
  turbulence,
  particleColor1,
  particleColor2,
  particleColor3,
  particleColor4,
  particleColorCount,
  onInitialized,
  onAnimationStopped,
}: UseParticleRendererProps) => {
  const [workerState, setWorkerState] = useState<'idle' | 'pending' | 'ready'>(
    'idle',
  );
  const pendingPlayResolversRef = useRef<Array<() => void>>([]);
  const currentPlayResolverRef = useRef<(() => void) | null>(null);

  const playRef = useRef<() => Promise<void>>(async () => {});
  const stopRef = useRef<() => void>(() => {});
  const pauseRef = useRef<() => void>(() => {});

  const processPendingPlayCalls = useCallback(() => {
    if (
      pendingPlayResolversRef.current.length &&
      !currentPlayResolverRef.current
    ) {
      const nextResolve = pendingPlayResolversRef.current.shift();
      if (nextResolve) {
        currentPlayResolverRef.current = nextResolve;
        worker?.postMessage(messages.getPlayMessage());
      }
    }
  }, [worker, messages]);

  const handleAnimationStopped = useCallback(() => {
    if (currentPlayResolverRef.current) {
      currentPlayResolverRef.current();
      currentPlayResolverRef.current = null;
    }

    pendingPlayResolversRef.current = [];
    onAnimationStopped();
  }, [onAnimationStopped]);

  const messageHandler = useCallback(
    (message: MessageEvent<WorkerMessage>) => {
      const { data } = message;
      const workerMessageHandler: Record<WorkerMessageTypes, () => void> = {
        [INITIALIZED]: () => {
          onInitialized();

          setWorkerState('ready');
          processPendingPlayCalls();
        },
        [ANIMATION_STOPPED]: () => {
          handleAnimationStopped();
        },
        [ANIMATION_FINISHED]: () => {
          onAnimationEnd();
          if (currentPlayResolverRef.current) {
            currentPlayResolverRef.current();
            currentPlayResolverRef.current = null;
          }

          processPendingPlayCalls();
        },
      };

      if (componentId === data.componentId) {
        workerMessageHandler[data.type]?.();
      }
    },
    [
      componentId,
      onAnimationEnd,
      processPendingPlayCalls,
      onInitialized,
      handleAnimationStopped,
    ],
  );

  useEffect(() => {
    if (!worker) {
      return;
    }

    worker.addEventListener('message', messageHandler);

    return () => {
      worker.removeEventListener('message', messageHandler);
    };
  }, [messageHandler, worker]);

  useEffect(() => {
    if (!worker || workerState !== 'ready') {
      return;
    }

    worker.postMessage(
      messages.getUpdateRendererPropsMessage({
        text,
        radius,
        duration,
        animationType,
        direction,
        particleStartSize,
        particleEndSize,
        particleStartOpacity,
        particleEndOpacity,
        windDirection,
        windSpeed,
        turbulence,
        particleColor1,
        particleColor2,
        particleColor3,
        particleColor4,
        particleColorCount,
      }),
    );
  }, [
    worker,
    workerState,
    messages,
    animationType,
    radius,
    duration,
    direction,
    particleStartSize,
    particleEndSize,
    particleStartOpacity,
    particleEndOpacity,
    windDirection,
    windSpeed,
    turbulence,
    particleColor1,
    particleColor2,
    particleColor3,
    particleColor4,
    particleColorCount,
    text,
  ]);
  useEffect(() => {
    if (
      !canvasRef.current ||
      !worker ||
      workerState === 'ready' ||
      workerState === 'pending' ||
      !image ||
      isImageTransferred(image)
    ) {
      return;
    }

    setWorkerState('pending');
    const transferredCanvas = canvasRef.current.transferControlToOffscreen();

    worker.postMessage(
      messages.getInitializeMessage({
        imageBitmap: image,
        canvas: transferredCanvas,
        particleRendererProps: {
          text,
          radius,
          duration,
          animationType,
          direction,
          particleStartSize,
          particleEndSize,
          particleStartOpacity,
          particleEndOpacity,
          windDirection,
          windSpeed,
          turbulence,
          particleColor1,
          particleColor2,
          particleColor3,
          particleColor4,
          particleColorCount,
        },
      }),
      [image, transferredCanvas],
    );
  }, [
    animationType,
    canvasRef,
    workerState,
    image,
    worker,
    componentId,
    messages,
    text,
    radius,
    duration,
    direction,
    particleStartSize,
    particleEndSize,
    particleStartOpacity,
    particleEndOpacity,
    windDirection,
    windSpeed,
    turbulence,
    particleColor1,
    particleColor2,
    particleColor3,
    particleColor4,
    particleColorCount,
  ]);

  useEffect(() => {
    if (
      !worker ||
      workerState !== 'ready' ||
      !image ||
      isImageTransferred(image)
    ) {
      return;
    }

    worker.postMessage(messages.getUpdateImageMessage({ imageBitmap: image }), [
      image,
    ]);
  }, [componentId, image, workerState, worker, messages]);

  useEffect(() => {
    playRef.current = async () =>
      new Promise<void>(resolve => {
        pendingPlayResolversRef.current.push(resolve);

        if (workerState === 'ready' && !currentPlayResolverRef.current) {
          processPendingPlayCalls();
        }
      });
  }, [processPendingPlayCalls, workerState]);

  useEffect(() => {
    if (!worker || workerState !== 'ready') {
      return;
    }

    stopRef.current = () => {
      worker.postMessage(messages.getStopMessage());
    };

    pauseRef.current = () => {
      worker.postMessage(messages.getPauseMessage());

      if (currentPlayResolverRef.current) {
        currentPlayResolverRef.current();
        currentPlayResolverRef.current = null;
      }

      pendingPlayResolversRef.current = [];
    };
  }, [worker, workerState, messages, handleAnimationStopped]);

  const play = useCallback(async () => playRef.current(), []);

  const stop = useCallback(() => stopRef.current(), []);

  const pause = useCallback(() => pauseRef.current(), []);

  return {
    play,
    stop,
    pause,
  };
};
